import { useMemo } from 'react';
import { useLanguage } from './useLanguage';

/**
 * @hook useBrand
 * @description One normalised view of the branding document.
 *
 *   Everything here is editable in Dictionary → Global Settings → Brand & meta.
 *
 *   This exists because the branding document moved its site name under META
 *   while a dozen call sites still read `brand?.NAME`. That key does not exist,
 *   so every one of them silently fell through to a hardcoded 'Moksy' and the
 *   Site name field did nothing. Reading through this hook means a field only
 *   has to be resolved correctly once.
 *
 *   Legacy top-level keys are still consulted as a second choice so older
 *   documents (and anything seeded before the manifest existed) keep working.
 */

const FALLBACK_NAME = 'Moksy';

// Flat FOOTER_COLUMNS rows ({ column, label, href }) → grouped columns
// ([{ title, links: [{ label, href }] }]) preserving first-seen order. Kept
// flat in the schema so the dictionary editor can manage it with one repeater.
const groupColumns = (rows) => {
  if (!Array.isArray(rows)) return [];
  const order = [];
  const byTitle = new Map();
  for (const row of rows) {
    const title = String(row?.column || '').trim();
    const label = String(row?.label || '').trim();
    const href = String(row?.href || '').trim();
    if (!title || !label || !href) continue;
    if (!byTitle.has(title)) { byTitle.set(title, []); order.push(title); }
    byTitle.get(title).push({ label, href });
  }
  return order.map((title) => ({ title, links: byTitle.get(title) }));
};

export const useBrand = () => {
  const brand = useLanguage('branding');

  return useMemo(() => {
    const meta = brand?.META || {};
    const logo = brand?.LOGO || {};
    const social = brand?.SOCIAL_LINKS || {};

    return {
      raw: brand,

      // Identity
      name: meta.NAME || brand?.NAME || FALLBACK_NAME,
      tagline: brand?.TAGLINE || '',
      title: meta.TITLE || meta.NAME || FALLBACK_NAME,
      description: meta.DESCRIPTION || '',

      // Imagery
      logo: logo.PRIMARY || logo.SECONDARY || '',
      verticalLogo: logo.VBLOGO || logo.PRIMARY || '',
      monoIcon: logo.BICON || '',
      favicon: logo.FAVICON || logo.ICON || '',
      touchIcon: logo.TOUCH_ICON || logo.FAVICON || logo.ICON || '',
      shareImage: logo.SOCIAL || logo.PRIMARY || '',

      // Footer furniture
      copyright: brand?.COPYRIGHT || '',
      documentationText: brand?.DOCUMENTATION_TEXT || '',
      documentationLink: brand?.DOCUMENTATION_LINK || '',
      version: brand?.VERSION || '',
      footerLinks: Array.isArray(brand?.FOOTER_LINKS) ? brand.FOOTER_LINKS : [],

      // Marketing (public) footer content — all editable in Brand & meta.
      footerTagline: brand?.FOOTER_TAGLINE || brand?.TAGLINE || '',
      footerCta: brand?.FOOTER_CTA_LABEL
        ? { label: brand.FOOTER_CTA_LABEL, href: brand.FOOTER_CTA_LINK || '/register' }
        : null,
      footerBanner: (brand?.FOOTER_BANNER_IMAGE || brand?.FOOTER_BANNER_TEXT)
        ? { image: brand?.FOOTER_BANNER_IMAGE || '', text: brand?.FOOTER_BANNER_TEXT || '', href: brand?.FOOTER_BANNER_LINK || '' }
        : null,
      footerCompany: brand?.FOOTER_COMPANY_INFO || '',
      footerLegalLine: brand?.FOOTER_LEGAL_LINE || '',
      footerColumns: groupColumns(brand?.FOOTER_COLUMNS),
      footerLegalLinks: Array.isArray(brand?.FOOTER_LEGAL_LINKS) ? brand.FOOTER_LEGAL_LINKS : [],

      /** Configured social profiles only — empty entries are dropped. */
      socialLinks: [
        { id: 'linkedin', label: 'LinkedIn', href: social.LINKEDIN },
        { id: 'twitter', label: 'Twitter/X', href: social.TWITTER },
        { id: 'facebook', label: 'Facebook', href: social.FACEBOOK },
        { id: 'instagram', label: 'Instagram', href: social.INSTAGRAM }
      ].filter((item) => String(item.href || '').trim() !== '')
    };
  }, [brand]);
};

export default useBrand;
